"use client"

import { useEffect, useState } from "react"
import { Smartphone } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"

import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { analytics, analysisFunnelEnvelope, trackEvent } from "@/lib/analytics"
import { runWhenAnalyticsReady } from "@/lib/analytics/analytics-ready"
import { adoptAnalysisFunnelId, ensureAnalysisFunnelId } from "@/lib/analysis-funnel-id"

type ScanMode = "identify" | "diagnose"

const HANDOFF_LOCATION = "desktop_scan_handoff"
const HANDOFF_LANDING_SESSION_KEY = "desktop_scan_handoff_landed"

function handoffUrl(mode: ScanMode, funnelId?: string): string {
  const destination = new URL(
    mode === "diagnose" ? "/diagnose" : "/",
    "https://whatgrassisthis.com",
  )
  destination.searchParams.set("handoff", "desktop_scan")
  destination.searchParams.set("mode", mode)
  destination.searchParams.set("utm_source", "desktop_qr")
  destination.searchParams.set("utm_medium", "qr")
  if (funnelId) destination.searchParams.set("funnel_id", funnelId)
  return destination.toString()
}

interface DesktopScanHandoffProps {
  mode: ScanMode
  pagePath: string
}

/**
 * A desktop-to-phone bridge for the photo step.
 *
 * The upload button remains primary. This compact rail only appears for fine
 * pointers, where PostHog shows the largest picker abandonment. The QR carries
 * the current funnel id so the phone landing and its later upload stay joined
 * to the desktop impression.
 */
export function DesktopScanHandoff({ mode, pagePath }: DesktopScanHandoffProps) {
  const [sourceFunnelId, setSourceFunnelId] = useState<string | null>(null)
  const [destinationUrl, setDestinationUrl] = useState(() => handoffUrl(mode))

  // This same component mounts on the phone destination. Adopt the desktop id
  // before the upload card can mint a new one, then record the cross-device
  // arrival once for this tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("handoff") !== "desktop_scan") return

    const requestedMode = params.get("mode")
    const landedMode: ScanMode =
      requestedMode === "diagnose" || requestedMode === "identify" ? requestedMode : mode
    const requestedFunnelId = params.get("funnel_id")
    const adoptedFunnelId = adoptAnalysisFunnelId(requestedFunnelId)
    const dedupKey = `${HANDOFF_LANDING_SESSION_KEY}:${adoptedFunnelId}:${landedMode}`

    // posthog.init runs in PHProvider's mount effect, which React flushes
    // after this leaf effect, so the capture waits for analytics to be ready.
    // The dedupe key is written only once the capture call has been made.
    return runWhenAnalyticsReady(() => {
      try {
        if (window.sessionStorage.getItem(dedupKey)) return
      } catch {
        // A duplicate landing event is preferable to losing the handoff entirely.
      }

      trackEvent("scan_phone_handoff_landed", {
        ...analysisFunnelEnvelope({
          funnelId: adoptedFunnelId,
          mode: landedMode,
          source: HANDOFF_LOCATION,
          imageRoles: ["primary"],
          actualDestination: "upload_card",
        }),
        cta_location: HANDOFF_LOCATION,
        page_path: pagePath,
        handoff_funnel_joined: requestedFunnelId === adoptedFunnelId,
        origin_device: "desktop",
        destination_device: "phone",
      })

      try {
        window.sessionStorage.setItem(dedupKey, "1")
      } catch {
        // Storage unavailable; the event already went out.
      }
    })
  }, [mode, pagePath])

  useEffect(() => {
    const funnelId = ensureAnalysisFunnelId()
    setSourceFunnelId(funnelId)
    setDestinationUrl(handoffUrl(mode, funnelId))
  }, [mode])

  const impressionRef = useImpressionEvent<HTMLDivElement>(
    HANDOFF_LOCATION,
    {
      funnel_id: sourceFunnelId,
      mode,
      page_path: pagePath,
      destination_path: mode === "diagnose" ? "/diagnose" : "/",
      origin_device: "desktop",
      destination_device: "phone",
    },
    {
      enabled: Boolean(sourceFunnelId),
      emit: analytics.ctaSurfaceShownWhenVisible,
    },
  )

  const resultLabel = mode === "diagnose" ? "lawn diagnosis" : "grass ID"

  return (
    <div
      ref={impressionRef}
      className="mx-auto mt-5 hidden max-w-md rounded-2xl border border-green-200 bg-green-50/70 p-3 text-left [@media(pointer:fine)]:block"
    >
      <div className="flex items-center gap-3.5">
        <div className="shrink-0 rounded-xl border border-green-200 bg-white p-2 shadow-sm">
          <QRCodeSVG
            value={destinationUrl}
            size={104}
            level="M"
            marginSize={1}
            fgColor="#14532d"
            title={`Open the ${resultLabel} photo step on your phone`}
          />
        </div>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-bold text-green-950">
            <Smartphone className="h-4 w-4 shrink-0 text-green-700" aria-hidden="true" />
            Photo is on your phone?
          </p>
          <p className="mt-1 text-[13px] leading-snug text-green-900/80">
            Scan this code to open the same {resultLabel} flow and take a fresh photo.
          </p>
        </div>
      </div>
    </div>
  )
}
